"use client";

import { useState, useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PortfolioPreview() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const [api, setApi] = useState<any>(null);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const portfolioItems = [{
    id: 1,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_crash_repair_before_1.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_crash_repair_after_1.png",
    title: "Frame Restoration"
  }, {
    id: 2,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_panel_respray_before_2.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_panel_respray_after_2.png",
    title: "Panel Respray"
  }, {
    id: 3,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_dent_removal_before_3.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_dent_removal_after_3.png",
    title: "Dent Removal"
  }, {
    id: 4,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_custom_graphics_before_4.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_custom_graphics_after_4.png",
    title: "Custom Graphics"
  }, {
    id: 5,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_structural_repair_before_5.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_structural_repair_after_5.png",
    title: "Structural Repair"
  }, {
    id: 6,
    beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_paint_touch_up_before_6.png",
    afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/motorcycle_paint_touch_up_after_6.png",
    title: "Paint Touch-Up"
  }];
  useEffect(() => {
    if (!api) return;
    const startAutoPlay = () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      autoPlayRef.current = setInterval(() => {
        if (!isHovered) {
          api.scrollNext();
        }
      }, 4000);
    };
    startAutoPlay();
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [api, isHovered]);
  return <section ref={ref} className={`py-16 md:py-24 bg-background transition-opacity duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {}
          <div className={`transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Carousel setApi={setApi} opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                {portfolioItems.map((item, i) => <CarouselItem key={item.id} data-index={i}>
                    <BeforeAfterSlider beforeImage={item.beforeImage} afterImage={item.afterImage} title={item.title} />
                  </CarouselItem>)}
              </CarouselContent>
              <div className="flex justify-center mt-6 gap-4">
                <CarouselPrevious className="relative left-0 top-0 translate-y-0" />
                <CarouselNext className="relative right-0 top-0 translate-y-0" />
              </div>
            </Carousel>
          </div>

          {}
          <div className={`space-y-6 transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
            <div className="space-y-3">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
                Our Portfolio
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Explore our collection of expertly restored motorcycles. From
                crash repairs to custom resprays, each project showcases our
                commitment to precision and quality craftsmanship.
              </p>
            </div>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              Drag the slider on each image to reveal the transformation from
              damaged to fully restored. Our experienced technicians use
              OEM-grade materials and advanced techniques to bring every bike
              back to life.
            </p>
            <div className="pt-4">
              <Button asChild className="btn-primary group">
                <Link to="/PortfolioPage">
                  View Full Portfolio
                  <ChevronRight className="ml-1 transition-transform duration-[var(--duration-medium)] group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function BeforeAfterSlider({
  beforeImage,
  afterImage,
  title
}: {
  beforeImage: string;
  afterImage: string;
  title: string;
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = x / rect.width * 100;
    setSliderPosition(Math.max(0, Math.min(100, percentage)));
  };
  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);
  return <div ref={containerRef} className="relative w-full aspect-[4/3] overflow-hidden rounded-lg shadow-medium cursor-col-resize select-none" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp}>
      {}
      <div className="absolute inset-0">
        <img src={afterImage} alt={`${title} - After`} className="w-full h-full object-cover" />
        <div className="absolute top-4 right-4 bg-primary text-foreground-primary px-3 py-1.5 rounded-sm text-xs font-semibold shadow-soft">
          After
        </div>
      </div>

      {}
      <div className="absolute inset-0 overflow-hidden" style={{
      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
    }}>
        <img src={beforeImage} alt={`${title} - Before`} className="w-full h-full object-cover" />
        <div className="absolute top-4 left-4 bg-secondary text-foreground-secondary px-3 py-1.5 rounded-sm text-xs font-semibold shadow-soft">
          Before
        </div>
      </div>

      {}
      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-strong cursor-col-resize z-10" style={{
      left: `${sliderPosition}%`
    }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-strong flex items-center justify-center">
          <div className="flex gap-1">
            <div className="w-0.5 h-4 bg-foreground"></div>
            <div className="w-0.5 h-4 bg-foreground"></div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <h3 className="text-white font-display font-semibold text-lg">
          {title}
        </h3>
      </div>
    </div>;
}